/**
 * **i18n 运行时。**
 *
 * 所有玩家可见文本都走 `t(key)`；键与中英译文分命名空间放在 `./zh/*` 与
 * `./en/*`，由 `./zh.ts` / `./en.ts` 汇总。**这是唯一的翻译入口**。
 *
 * ## i18n 的值是 `TextSpec`，不是裸字符串
 *
 * 字典里每一条都是 `TextComponent[]`（见 `./text.ts`）：纯文本、牌引用、术语、
 * 动态值（`param`）、或嵌套的键。**文案里不留 `{name}` 占位符，也不留 markup**。
 *
 * - `t(key, params)` → **纯文本**（给 label / 按钮 / 日志用）。
 * - `richText(key, params)` → **Pixi tagged text**（带颜色标签，给富文本渲染用）。
 * - `bi(key, params)` → 双语标签「中文 / English」。
 *
 * ## 语言从哪来
 *
 * 语言存在玩家设置里（`ui/settings.ts` 的 `Settings.lang`），初始值跟随
 * 浏览器（`detectLang(navigator.language)`）。运行时用 `setLang()` 同步过来；
 * 语言变化时 `screenHost` 会像 UI 倍率那样整棵重建。
 */

import { zh, type TextKey } from './zh';
import { en } from './en';
import { textPlain, textTagged, type TextEnv, type TextParams, type TextSpec } from './text';

export type Lang = 'zh' | 'en';

/** 支持的界面语言，顺序就是设置页里显示的顺序 */
export const LANGS: readonly Lang[] = ['zh', 'en'];

/** 语言自称（切换按钮上显示的） */
export const LANG_NAMES: Record<Lang, string> = {
  zh: '中文',
  en: 'English',
};

/** 浏览器语言标签 → 支持的语言。非中文一律英语 */
export function detectLang(tag: string | null | undefined): Lang {
  return (tag ?? '').toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

const DICTS: Record<Lang, Record<string, TextSpec>> = { zh, en };
const ZH = zh as unknown as Record<string, TextSpec>;

let currentLang: Lang = 'zh';

export function setLang(lang: Lang): void {
  currentLang = lang;
}

export function getLang(): Lang {
  return currentLang;
}

function specOf(key: TextKey): TextSpec {
  return DICTS[currentLang][key as string] ?? ZH[key as string] ?? [key as string];
}

/**
 * 取一条译文的**纯文本**。
 *
 * `params` 填的是字典里的 `param` 片段：`t('room.playersCount', { n: 3, max: 4 })`。
 * 找不到的键**原样返回键名**（好过空白 —— 截图里一眼能看出漏翻）。
 */
export function t(key: TextKey, params?: TextParams): string {
  return textPlain(specOf(key), params ?? {}, env);
}

/**
 * 同一条译文的 **Pixi tagged text**（颜色标签已就位）。
 *
 * 用 `render/ui/richText.ts` 的 `richLabel` 渲染；颜色标签的样式会**盖过**
 * 基础 `label` 样式，所以富文本颜色优先。
 */
export function richText(key: TextKey, params?: TextParams): string {
  return textTagged(specOf(key), params ?? {}, env);
}

/** `TextComponent` 里的 `key` 片段 / `term` 片段由它俩解析（递归） */
const env: TextEnv = {
  key: (key, params, rich) => (rich ? richText(key, params) : t(key, params)),
  // 术语表住在 `lore.term.*`（这一层不是 lore 专用，只是键名沿用了历史前缀）
  term: (name) => t(('lore.term.' + name) as TextKey),
};

/**
 * **双语标签**：`中文 / English`。
 *
 * 中文界面里标题/页眉习惯写成「中文 / ENGLISH」；英文界面里就只给英文。
 * ⚠️ zh 字典里**只许放中文**，英文从 en 字典取。
 */
export function bi(key: TextKey, params?: TextParams): string {
  const zhSpec = ZH[key as string] ?? [key as string];
  const enSpec = en[key as string] ?? zhSpec;
  const zhText = textPlain(zhSpec, params ?? {}, env);
  const enText = textPlain(enSpec, params ?? {}, env);
  return currentLang === 'en' ? enText : zhText + ' / ' + enText;
}

export type { TextKey };
export { zh, en };
export { textPlain, textTagged } from './text';
export type { TextColor, TextComponent, TextParams, TextSpec } from './text';
